import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

/**
 * State for the Infinity Touch CUSTOMER site (customer.infinitytouch.app):
 * global theme/config, the passwordless pin session, and a lightweight cart.
 * Isolated from the edisplayroom StoreContext/CartContext (which are subdomain
 * scoped) so the single-domain IT customer flow stays self-contained.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api/infinity/customer`;
const TOKEN_KEY = "it_customer_token";
const CART_KEY = "it_customer_cart";
const ADDR_KEY = "it_customer_addresses";
// The "All Products" tab is hidden by default (TR-22): families come here for
// fingerprint jewelry, and the platform owner only wants the wider eDisplayroom
// range offered once an order is placed. Clicking "See our full range of products"
// on the thank-you screen sets this, unhiding the tab for the rest of the session.
// sessionStorage, not localStorage, so it lapses when the tab closes.
const SHOW_ALL_KEY = "it_customer_show_all_products";

const InfinityCustomerContext = createContext(null);

// On the real host the app lives at "/"; in dev/preview it's mounted under
// "/it-customer" so it can be exercised without the production domain.
export function itBasePath() {
  return window.location.hostname === "customer.infinitytouch.app" ? "" : "/it-customer";
}

export function InfinityCustomerProvider({ children }) {
  const [config, setConfig] = useState(null); // { theme, stripe_publishable_key }
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [customer, setCustomer] = useState(null);
  const [store, setStore] = useState(null);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false); // slide-out cart drawer (TR-8)
  // Held in state as well as sessionStorage so unlocking re-renders the header
  // immediately — a bare sessionStorage write would not.
  const [showAllProducts, setShowAllProducts] = useState(() => {
    try { return sessionStorage.getItem(SHOW_ALL_KEY) === "1"; } catch { return false; }
  });
  // Saved addresses persist on the family member's customer record, so the book
  // follows them to another device. Checkout prefills from the default address.
  const [addresses, setAddresses] = useState([]);

  // A dedicated axios instance that carries the pin-session token.
  const http = useMemo(() => {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use((cfg) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) cfg.headers.Authorization = `Bearer ${t}`;
      return cfg;
    });
    return inst;
  }, []);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => setConfig({ theme: {} }));
  }, []);

  // Hydrate the resolved store AND the session customer on load (e.g. a hard
  // refresh on /checkout) — keeps the "Urn store" nav link, channel gating and
  // the checkout contact prefill reliable without needing to visit Home first.
  useEffect(() => {
    if (token && !store) {
      http.get("/case").then((r) => {
        if (r.data?.store) setStore(r.data.store);
        if (r.data?.customer) setCustomer(r.data.customer);
      }).catch(() => {});
    }
  }, [token]); // eslint-disable-line

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  // Load the saved address book once a session exists, carrying over anything
  // this device saved before addresses moved server-side.
  useEffect(() => {
    if (!token) { setAddresses([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await http.get("/addresses");
        const serverList = r.data || [];
        if (cancelled) return;

        let local = [];
        try { local = JSON.parse(localStorage.getItem(ADDR_KEY) || "[]"); } catch { local = []; }
        if (serverList.length === 0 && local.length > 0) {
          const migrated = await http.put("/addresses", { addresses: local });
          if (cancelled) return;
          setAddresses(migrated.data || []);
        } else {
          setAddresses(serverList);
        }
        localStorage.removeItem(ADDR_KEY);
      } catch {
        if (!cancelled) setAddresses([]);
      }
    })();
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line

  // Every mutation PUTs the whole book back; the server settles the default flag.
  const persistAddresses = useCallback(async (next) => {
    const r = await http.put("/addresses", { addresses: next });
    setAddresses(r.data || []);
    return r.data;
  }, [http]);

  const saveAddress = useCallback((addr) => {
    const makeDefault = addresses.length === 0 || addr.is_default;
    const next = [
      ...addresses.map((a) => (makeDefault ? { ...a, is_default: false } : a)),
      { ...addr, is_default: makeDefault },
    ];
    return persistAddresses(next);
  }, [addresses, persistAddresses]);

  const deleteAddress = useCallback(
    (id) => persistAddresses(addresses.filter((a) => a.id !== id)),
    [addresses, persistAddresses]
  );

  const setDefaultAddress = useCallback(
    (id) => persistAddresses(addresses.map((a) => ({ ...a, is_default: a.id === id }))),
    [addresses, persistAddresses]
  );

  const login = useCallback(async ({ order_id, pin, name, phone, email }) => {
    const r = await axios.post(`${API}/login`, { order_id, pin, name, phone, email });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setCustomer(r.data.customer);
    setStore(r.data.store);
    return r.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(CART_KEY);
    // Re-lock the wider catalog: the next family to sign in on this tab starts
    // from the fingerprint-jewelry-only view again.
    try { sessionStorage.removeItem(SHOW_ALL_KEY); } catch { /* ignore */ }
    setToken("");
    setCustomer(null);
    setStore(null);
    setCart([]);
    setShowAllProducts(false);
  }, []);

  const addToCart = useCallback((item) => {
    setCart((c) => [...c, { ...item, key: `${item.product.product_id}_${Date.now()}` }]);
    setCartOpen(true); // open the slide-out drawer on add (edisplayroom parity)
  }, []);
  const updateQty = useCallback((key, delta) => {
    setCart((c) => c.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i)));
  }, []);
  const unlockAllProducts = useCallback(() => {
    try { sessionStorage.setItem(SHOW_ALL_KEY, "1"); } catch { /* private mode — state still unlocks it */ }
    setShowAllProducts(true);
  }, []);
  const removeItem = useCallback((key) => setCart((c) => c.filter((i) => i.key !== key)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const value = {
    config,
    theme: config?.theme || {},
    loginSlider: config?.login_slider || [],
    featuredProducts: config?.featured_products || [],
    stripePublishableKey: config?.stripe_publishable_key,
    token,
    isAuthed: Boolean(token),
    customer,
    store,
    setStore,
    hasEdisplay: Boolean(store?.has_edisplay_module),
    showAllProducts,
    unlockAllProducts,
    http,
    login,
    logout,
    cart,
    cartCount: cart.reduce((n, i) => n + i.quantity, 0),
    cartOpen,
    setCartOpen,
    addToCart,
    updateQty,
    removeItem,
    clearCart,
    addresses,
    saveAddress,
    deleteAddress,
    setDefaultAddress,
    basePath: itBasePath(),
  };

  return <InfinityCustomerContext.Provider value={value}>{children}</InfinityCustomerContext.Provider>;
}

export function useInfinityCustomer() {
  const ctx = useContext(InfinityCustomerContext);
  if (!ctx) throw new Error("useInfinityCustomer must be used within InfinityCustomerProvider");
  return ctx;
}
